<script setup lang="ts">
// App-wide billing state bar (issue 213). The lib owns the LOOK; every string is
// passed in as a prop so each app localizes (no copy baked in). It is NOT
// dismissible — billing state is not something the user can wave away.
//
// One state, one skin: the enforcement ladder is warning → blocked, so the only
// thing this bar ever says is "this workspace will be locked on <date>". That is
// the last warning before lockout, so it wears the danger surface for everyone —
// the neutral read-only variant went with the `degraded` stage. The CTA is
// optional: only a billing manager gets one (a regular member would 403). It is a
// link (`ctaHref`, whose click `ctaAction` may take over for an in-app navigation)
// or, with `ctaAction` and no href, a button that runs the action — the SCA
// recovery payment. `ctaBusy` disables that button while the action runs and
// `errorText` is the short inline line a failed action leaves under the message.
import LumiIcon from '../icons/LumiIcon.vue';
defineProps<{
  title: string;
  message: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAction?: (event?: MouseEvent) => void;
  ctaBusy?: boolean;
  errorText?: string;
}>();
</script>

<template>
  <div class="lumi-billing-banner" role="alert">
    <span class="lumi-billing-banner__icon" aria-hidden="true">
      <LumiIcon name="warning" width="18" height="18" stroke-width="2" />
    </span>
    <div class="lumi-billing-banner__content">
      <p class="lumi-billing-banner__title">{{ title }}</p>
      <p class="lumi-billing-banner__message">{{ message }}</p>
      <p v-if="errorText" class="lumi-billing-banner__error">{{ errorText }}</p>
    </div>
    <a
      v-if="ctaLabel && ctaHref"
      class="lumi-billing-banner__cta"
      :href="ctaHref"
      @click="ctaAction?.($event)"
    >{{ ctaLabel }}</a>
    <button
      v-else-if="ctaLabel && ctaAction"
      type="button"
      class="lumi-billing-banner__cta"
      :disabled="ctaBusy"
      :aria-busy="ctaBusy ? 'true' : undefined"
      @click="ctaAction($event)"
    >{{ ctaLabel }}</button>
  </div>
</template>

<style>
/* Tokens only (no tailwind utilities): these SPAs don't reliably process the
   danger-* utility classes, so the surface is set via CSS vars (matches the
   app-kit DemoStageBar). */
.lumi-billing-banner{display:flex;align-items:center;gap:var(--space-3);width:100%;padding:var(--space-2) var(--space-4);font-family:var(--font-body);background:var(--danger-soft);color:var(--danger-text);}
.lumi-billing-banner__icon{flex:none;display:flex;align-items:center;padding-top:1px;}
.lumi-billing-banner__content{flex:1;min-width:0;}
.lumi-billing-banner__title{font-size:var(--text-sm);font-weight:var(--weight-semibold);margin:0;line-height:1.3;}
.lumi-billing-banner__message{font-size:var(--text-sm);margin:0;line-height:1.4;}
.lumi-billing-banner__cta{flex:none;font-size:var(--text-sm);font-weight:var(--weight-semibold);text-decoration:none;padding:var(--space-1) var(--space-3);border-radius:var(--radius-md);border:1px solid currentColor;white-space:nowrap;}
.lumi-billing-banner__error{font-size:var(--text-sm);font-weight:var(--weight-semibold);margin:var(--space-1) 0 0;line-height:1.4;}
button.lumi-billing-banner__cta{font-family:inherit;background:transparent;color:inherit;cursor:pointer;}
.lumi-billing-banner__cta:hover{opacity:.85;}
.lumi-billing-banner__cta:disabled{opacity:.6;cursor:progress;}
/* On a phone the strip stays pinned above the page, so it takes as little of the
   screen as it can: tighter, smaller type, the action under the text. */
@media (max-width:767px){
  .lumi-billing-banner{flex-wrap:wrap;align-items:flex-start;gap:var(--space-1) var(--space-2);padding:var(--space-2) var(--space-3);}
  .lumi-billing-banner__content{flex-basis:calc(100% - 18px - var(--space-2));}
  .lumi-billing-banner__title,.lumi-billing-banner__message,.lumi-billing-banner__error{font-size:var(--text-xs);}
  .lumi-billing-banner__cta{margin-left:calc(18px + var(--space-2));font-size:var(--text-xs);}
}
</style>
